import DSGLogoLarge from "@DSG/Icons/DSGLogoLarge";
import ChevronDown from "@DSG/MobiusOverrides/Icons/ChevronDown";
import colorPallete, {
    bodyFonts,
    buttonFonts,
    labelFonts,
    themeFontWeights,
} from "@DSG/MobiusOverrides/styles/variables";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import X from "@insite/mobius/Icons/X";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";

const mapStateToProps = (state: ApplicationState) => {
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const orderSettings = getSettingsCollection(state).orderSettings;
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const isVmiAdmin = session?.userRoles?.toLowerCase().indexOf("vmi_admin") !== -1;
    const isVmiUser = session?.userRoles?.toLowerCase().indexOf("vmi_user") !== -1;
    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        isAuthenticated: state.context.session.isAuthenticated,
        isSessionCreated: state.context.session?.userRoles,
        pageTitle: getCurrentPage(state).fields.title,
        cart: getCurrentCartState(state).value,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        displayVmiNavigation: currentMode === "Vmi" && orderSettings.vmiEnabled && isAuthenticated && (isVmiAdmin || isVmiUser),

    }
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    subHeaderLinksStyles: InjectableCss;
    mainHeaderContainer: GridContainerProps;
    topHeader: GridContainerProps;
    nearestBranchInfo: GridItemProps;
    headerLinkList: GridItemProps;
    headerVMILinkList?: GridItemProps;
    mainHeader: GridContainerProps;
    headerLogo: GridItemProps;
    mainNavigation: GridItemProps;
    headerSearch: GridItemProps;
    headerReorder: GridItemProps;
    headerMyAccount: GridItemProps;
    subHeader: GridContainerProps;
    subHeaderInner: GridContainerProps;
    payMyBills: GridItemProps;
    dsgLinks: GridItemProps;
    dsgCart: GridItemProps;
    shippingGuestMessage: GridItemProps;
    page?: MobiusPageProps;
    vmiPageHeader?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    checkoutHeader?: InjectableCss;
    findLocationAnyWhere: GridContainerProps;
}

export const headerStyles: HeaderStyles = {
    subHeaderLinksStyles: {
        css: css`
            display: flex;
            align-items: center;
            > [class*="GridItemStyle-"] {
                max-width: unset;
                flex-basis: unset;
            }
        `,
    },
    mainHeaderContainer: {
        isFullWidth: true,
        css: css`
            &.hideOnCheckoutPage {
                display: none;
            }

            .shipAuthMsgWrapper {
                border-top: 1px solid ${colorPallete.grey200};
                background: ${colorPallete.grey100};
                width: 100%;
                padding: 8px 16px;

                &:not(.authMsg) {
                    min-height: 40px;
                    border: none;
                }
            }
        `,
    },
    topHeader: {
        css: css`
            padding: 8px 0px;

            @media only screen and (max-width: 1440px) {
                padding: 8px 24px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    nearestBranchInfo: {
        css: css`
            padding: 0;
            line-height: ${labelFonts.label1Regular.lineHeight};
        `,
    },
    headerLinkList: {
        css: css`
            line-height: ${labelFonts.label1Regular.lineHeight};
            padding: 0;
            .list-wrapper {
                padding: 0;
                margin: 0;
                .list-link {
                    &:not(:last-child) {
                        margin-right: 20px;
                    }
                }
                .links-wrapper {
                    padding: 0;
                    &:not(:last-child) {
                        margin-right: 20px;
                    }
                }
                .list-link,
                .list-link p {
                    font-family: ${labelFonts.label2SemiBold.fontFamily};
                    font-size: ${labelFonts.label2SemiBold.fontSize};
                    line-height: ${labelFonts.label2SemiBold.lineHeight};
                    text-transform: none;
                    margin: 0;
                    font-weight: ${themeFontWeights.semiBold};
                    @media only screen and (max-width: 1024px) {
                        font-size: ${labelFonts.label2SemiBold.fontSize};
                        line-height: ${labelFonts.label2SemiBold.lineHeight};
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        &.website-header-link-list {
                            > div {
                                display: flex;
                                justify-content: flex-end;
                                gap: 4px 0;
                                flex-wrap: wrap;
                                > div {
                                    display: flex;
                                    align-items: center;
                                }
                                > div {
                                    &:empty {
                                        display: none;
                                    }
                                }
                                > div:last-child:not(:first-child) {
                                    &:before {
                                        content: "";
                                        position: relative;
                                        display: flex;
                                        right: 0;
                                        top: 2px;
                                        height: 16px;
                                        width: 1px;
                                        margin: 0 20px; 
                                        background: ${colorPallete.grey600DisabledText};
                                    }
                                }
                            }
                        }
                    `,
                ],
                "min",
            )}
            @media screen and (min-width: 1024px) and (max-width: 1250px) {
                &.website-header-link-list {
                    > div {
                        .list-wrapper {
                            .links-wrapper {
                                &:not(:last-child) {
                                    margin-right: 12px;
                                }
                            }
                        }
                       
                        > div:last-child:not(:first-child) {
                            &:before {
                                margin: 0 12px; 
                            }
                        }
                    }
                }
            }
        `,
    },
    headerVMILinkList: {
        css: css`
            line-height: ${labelFonts.label1Regular.lineHeight};
            padding: 0;
            > div {
                display: flex;
                justify-content: flex-end;
            }
            .list-wrapper {
                padding: 0;
                margin: 0;
                .list-link {
                    &:not(:last-child) {
                        margin-right: 20px;
                    }
                }
                .links-wrapper {
                    padding: 0;
                    &:not(:last-child) {
                        margin-right: 20px;
                    }
                }
                .list-link,
                .list-link p {
                    font-family: ${labelFonts.label2SemiBold.fontFamily};
                    font-size: ${labelFonts.label2SemiBold.fontSize};
                    line-height: ${labelFonts.label2SemiBold.lineHeight};
                    text-transform: none;
                    margin: 0;
                    font-weight: ${themeFontWeights.semiBold};
                    @media only screen and (max-width: 1024px) {
                        font-size: ${labelFonts.label2SemiBold.fontSize};
                        line-height: ${labelFonts.label2SemiBold.lineHeight};
                    }
                }
            }          
        `
    },
    mainHeader: {
        isFullWidth: true,
        offsetCss: css`
            align-items: center;
        `,
        css: css`
            border-top: 1px solid ${colorPallete.grey200};
            border-bottom: 1px solid ${colorPallete.grey200};
            padding: 16px 24px;
            [class*="GridOffset-"] {
                align-items: center;
            }
            [class*="GridItemStyle-"] {
                padding: 0;
            }
            @media screen and (max-width: 768px) {
                padding: 12px 16px;
                min-height: 74px;
                display: flex;
                align-items: center;
            }
           
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            &.vmi-main-header {
                                > div {
                                    > div {
                                        > div {
                                            flex-wrap: nowrap;
                                        }
                                    }
                                }
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    headerLogo: {
        css: css`
            max-width: unset;
            flex-basis: unset;
            .wrapper {
                max-width: 100%;
                max-height: 100%;
                margin: 0 24px 0 0px;
            }
            &.dsgVmiLogo {
                img {
                    max-width: 60px;
                }
            }
            [class*="StyledButton-"] {
                display: flex;
                img {
                    display: block;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                null,
                css`
                    &.dsgVmiLogo {
                        img {
                            max-width: 88px;
                        }
                    }
                `,
            ], "min")}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    order: 0;
                    .wrapper {
                        margin: 0 16px;
                    }
                `,
            ], "max")}
        `,
    },
    mainNavigation: {
        css: css`
            max-width: unset;
            flex-basis: unset;
            .container {
                padding: 0;
                margin-right: 16px;
            }
            .item-wrapper {
                &:not(:last-child) {
                    margin-right: 16px;
                }
                &:first-child {
                    margin-left: 0;
                }
                span {
                    color: ${colorPallete.grey800};
                    font-family: ${bodyFonts.body2SemiBoldSm.fontFamily};
                    font-size: ${bodyFonts.body2SemiBoldSm.fontSize};
                    font-weight: ${bodyFonts.body2SemiBoldSm.fontWeight};
                    line-height: ${bodyFonts.body2SemiBoldSm.lineHeight};
                }

                [class*="IconWrapper-"] {
                    margin: 0;
                    margin-left: 8px;
                }

                [class*="StyledClickable-"] {
                    padding: 8px;
                    height: auto;

                    &:hover {
                        > span {
                            color: ${colorPallete.primaryRed500};

                            [class*="IconWrapper-"] {
                                svg {
                                    color: ${colorPallete.primaryRed500};
                                }
                            }
                        }
                    }
                }
            }

            [data-test-selector*="mainNavigationItem"] {
                [data-test-selector="menuItem"] {
                    [class*="MenuStyle-"] {
                        min-height: 100%;
                        height: auto;
                    }
                }
            }

            [data-test-selector="menuItemText"] {
                padding: 10px 40px;
                font-weight: ${themeFontWeights.regular} !important;
            }
            @media only screen and (max-width: 1023px) {
                order: -1;
            }
            @media screen and (min-width: 1024px) and (max-width: 1190px) {
                .item-wrapper {
                    > nav > [class*="StyledClickable-"],
                    > [class*="StyledClickable-"] {
                        padding: 8px 0;
                    }
                }
            }
        `,
    },
    headerSearch: {
        css: css`
            max-width: unset;
            flex-basis: auto;
            flex-grow: 1 !important;

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    flex-basis: 30%;
                `,
                null,
            ], "max")}
            @media screen and (min-width: 1024px) and (max-width: 1140px) {
                &.header-search {
                    flex-basis: 20%;
                }
            }
        `,
    },
    headerReorder: {
        css: css`
            max-width: unset;
            flex-basis: unset;
            [class*="OverflowWrapper-"] {
                display: flex;
                align-items: center;
                margin-left: 16px;
                margin-right: 16px;
                width: auto;
            }

            .activePopover {
                .reorder-active {
                    color: ${colorPallete.primaryRed500};

                    [class*="IconWrapper-"] {
                        transform: rotate(180deg);
                        svg {
                            color: ${colorPallete.primaryRed500};
                        }
                    }
                }
            }

            #header_MyListReorderLinks-popover {
                border: 1px solid ${colorPallete.grey300};
                border-radius: 0;
                border-bottom-left-radius: 8px;
                border-bottom-right-radius: 8px;
                overflow: hidden;
                transition: none;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    headerMyAccount: {
        css: css`
            max-width: unset;
            flex-basis: unset;
             @media screen and (max-width: 768px) {
                > div[class*="Wrapper-"] {
                    margin-left: 16px;
                }
            } 
            &.vmi-header-account {
                .accountMenu {
                    padding-left: 24px;
                }
                [data-test-selector="header_signIn"] {
                    span {
                        color: ${colorPallete.white} !important;
                    }
                }
                .create-account-icon {
                    display: none;
                }
                .account-dropdown {
                    right: 0;
                    padding: 16px;
                    background: ${colorPallete.secondaryNavy500};
                    margin-top: 2px;
                    li {
                        display: flex;
                        justify-content: center;
                    }
                    button {
                        background: ${colorPallete.white};
                        border: 2px solid ${colorPallete.grey800};
                        min-height: 48px;
                        padding: 8px 22px;
                        max-width: 115px;
                        width: 100%;
                        border-radius: 30px;
                        font: ${buttonFonts.buttonLg.fontWeight} ${buttonFonts.buttonLg.fontSize}/${buttonFonts.buttonLg.lineHeight} ${buttonFonts.buttonLg.fontFamily};
                    }
                }
                @media screen and (min-width: 1024px) and (max-width: 1200px) {
                    .accountMenu {
                        padding-left: 16px;
                    }   
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            > div[class*="Wrapper-"] {
                                margin-left: 16px;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    subHeader: {
        isFullWidth: true,
        css: css`
            padding: 8px 16px;
            background: ${colorPallete.grey100};
            &.hideAlertBackground {
                height: 40px;
            }
            &.sub-header {
                min-height: 40px;
            }
            .list-wrapper {
                .links-wrapper {
                    display: flex;
                    line-height: 18px;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            padding: 4px 24px;
                            [data-test-selector="cartLink"] {
                                display: flex;
                                min-height: 31px;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subHeaderInner: {
        offsetCss: css`
            justify-content: space-between;
        `,
        css: css`
            .dsg-shipping-address {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                flex-basis: 100%;
                                max-width: unset;
                            `,
                        ],
                        "max",
                    )}
            }
            .vmi-address {
                [data-test-selector="header_shipToButton"] {
                    span {
                        color: ${colorPallete.white};
                    }
                    &[aria-expanded="true"] [data-test-selector="header_shipToLabel"] {
                        [class*="IconWrapper-"] svg {
                            stroke: ${colorPallete.white};
                        }
                        span:not(.branch-tag) {
                            color: ${colorPallete.white};
                        }
                    }
                }
            }
        `,
    },
    payMyBills: {
        css: css``,
    },
    dsgLinks: {
        css: css`
            .list-wrapper {
                padding: 0;
                .links-wrapper {
                    padding: 0;
                    line-height: ${labelFonts.label1Regular.lineHeight};
                }
                .list-link {
                    font-size: ${labelFonts.label1Regular.fontSize};
                    line-height: ${labelFonts.label1Regular.lineHeight};
                    color: ${colorPallete.grey800};
                    text-transform: none;
                    padding: 0 16px;
                    position: relative;

                    &::after {
                        content: "";
                        position: absolute;
                        right: 0;
                        height: 16px;
                        width: 1px;
                        background: #777779;
                    }
                }
            }
            &.vmiLinks {
                .list-wrapper {
                    .list-link {
                        padding: 0 20px;
                        span {
                            color: ${colorPallete.white};
                        }
                    }
                }
            }
        `,
    },
    dsgCart: {
        css: css`
            > div {
                margin-left: 16px;
            }
            a {
                height: auto;
            }
            &.hide-on-desktop {
                max-width: unset;
                flex-basis: unset;
            }
            &.vmiCart {
                
                [data-test-selector="cartLink"] {
                    span {
                        color: ${colorPallete.white};
                    }
                    [class*="IconWrapper"] {
                        svg {
                            color: ${colorPallete.white};
                        }
                    }
                    .hidden-cart-count-wrapper > [class*="Typography"],
                    [data-test-selector="cartLinkQuantity"] {
                        background: ${colorPallete.white};
                        color: ${colorPallete.grey800};
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        > div {
                            margin-left: 0;
                        }
                    `,
                    css`
                        > div {
                            margin-left: 20px;
                        }
                    `,
                ],
                "min",
            )}
        `,
    },
    shippingGuestMessage: {
        css: css`
            align-items: flex-start;
            max-width: 1440px;
            margin: 0 auto !important;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            align-items: center;
                        `,
                    ],
                    "min",
                )}

            .alert-icon {
                margin-right: 8px;
                cursor: pointer;
            }
            > [class*="Wrapper-"]:empty + [class*="IconWrapper"] {
                display: none;
                & + [class*="IconWrapper"] {
                    display: none;
                }
            }
            display: flex;
            > [class*="Wrapper-"] {
                order: 1;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                &:empty {
                                    padding: 0;
                                }
                            `,
                        ],
                        "min",
                    )}
            }

            &.authMsg {
                position: relative;
            }
            .alert-icon {
                order: 0;
            }
            .close-icon {
                order: 2;
            }
            p {
                font-size: ${bodyFonts.body2RegularLg.fontSize};
                line-height: ${bodyFonts.body2RegularLg.lineHeight};
                color: ${colorPallete.grey800};

                [class*="StyledClickable-"],
                [class*="StyledClickable-"] span {
                    font-size: ${bodyFonts.body2RegularLg.fontSize};
                    line-height: ${bodyFonts.body2RegularLg.lineHeight};
                    color: ${colorPallete.primaryRed500};
                    @media only screen and (max-width: 768px) {
                        font-size: ${bodyFonts.body2RegularLg.fontSize};
                        line-height: ${bodyFonts.body2RegularLg.lineHeight};
                    }
                }

                @media only screen and (max-width: 768px) {
                    font-size: ${bodyFonts.body2RegularLg.fontSize};
                    line-height: ${bodyFonts.body2RegularLg.lineHeight};
                }
            }
        `,
    },
    page: {
        fullWidth: [true, true, false, false, false],
        css: css`
            &.loggedInHeader {
                overflow: visible;
                .hideAlertBackground {
                    height: auto;
                }
            }
            &.checkoutPageHeader {
                background: ${colorPallete.grey50};
                padding: 16px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                padding: 29px 24px;
                            `,
                        ],
                        "min",
                    )}
                @media screen and (min-width: 1346px) {
                    padding-left: 80px;
                    padding-right: 80px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            .addToCartModalHeaderWrapper {
                                position: relative;
                                > div > [class*="GridItemStyle"] {
                                    padding: 0;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .addToCartModalHeaderWrapper {
                                position: absolute;
                                top: 84px;
                            }
                        `,
                    ],
                    "minMax",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            .addToCartModalHeaderWrapper {
                                position: absolute;
                                top: 60px;
                            }
                        `,
                    ],
                    "max",
                )}
            .addToCartModalHeaderWrapper {
                .addToCartModal {
                    display: none;
                    &.showAddToCartModal {
                        display: block;
                    }
                }
            }
            [class*="ZoneStyle"] {
                .addToCartModal {
                    display: block;
                }
            }
        `,
    },
    vmiPageHeader: {
        fullWidth: [true, true, false, false, false],
        css: css`
            &,
            .vmiSubHeader {
                background: ${colorPallete.secondaryNavy500};
            }
            .menu-trigger-button-icon svg {
                color: ${colorPallete.white};
            }
            .vmiSubHeader {
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            padding: 8px 24px;
                        `,
                    ],
                    "min",
                )}
            }
            &.loggedInHeader {
                overflow: visible;
                .hideAlertBackground {
                    height: auto;
                }
            }
            &.checkoutPageHeader {
                background: ${colorPallete.grey50};
                padding: 16px;
                &.vmiCheckout {
                    background: ${colorPallete.secondaryNavy500};
                    .back-to-cart {
                        [class*="IconWrapper"],
                        .text-link-primary,
                        .text-link-primary span[class*="StyledTypography-"] {
                            color: ${colorPallete.white};
                        }
                    }
                    .dsg-logo-large {
                        &, svg {
                            height: auto;
                            width: 60px;
                        }
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                padding: 29px 24px;
                                &.vmiCheckout {
                                    .dsg-logo-large {
                                        &, svg {
                                            width: 88px;
                                        }
                                    }
                                }
                            `,
                        ],
                        "min",
                    )}
                @media screen and (min-width: 1346px) {
                    padding-left: 80px;
                    padding-right: 80px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            .addToCartModalHeaderWrapper {
                                position: relative;
                                > div > [class*="GridItemStyle"] {
                                    padding: 0;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .addToCartModalHeaderWrapper {
                                position: absolute;
                                top: 84px;
                            }
                        `,
                    ],
                    "minMax",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            .addToCartModalHeaderWrapper {
                                position: absolute;
                                top: 60px;
                            }
                        `,
                    ],
                    "max",
                )}
            .addToCartModalHeaderWrapper {
                .addToCartModal {
                    display: none;
                    &.showAddToCartModal {
                        display: block;
                    }
                }
            }
            [class*="ZoneStyle"] {
                .addToCartModal {
                    display: block;
                }
            }
        `,
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    checkoutHeader: {
        css: css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: 1fr;
            align-items: center;
            align-content: center;
            margin-left: auto;
            margin-right: auto;
            max-width: 1280px;
            &.vmiCheckout {

            }
            .back-to-cart {
                &,
                span {
                    font: ${buttonFonts.buttonSm.fontWeight} ${buttonFonts.buttonSm.fontSize} /
                        ${buttonFonts.buttonSm.lineHeight} ${buttonFonts.buttonSm.fontFamily};
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            grid-template-columns: 0.82fr 1fr;
                        `,
                    ],
                    "min",
                )}

            .dsg-logo-large {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                justify-self: flex-end;
                                &,
                                svg {
                                    height: 24px;
                                    width: 73px;
                                }
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }

            [class*="IconWrapper"]:not(.dsg-logo-large) {
                transform: rotate(90deg);
            }

            [class*="StyledTypography"] {
                display: flex;
                align-items: center;
            }
        `,
    },

    findLocationAnyWhere: {
        offsetCss: css``,
        css: css`
            [class*="GridOffset"] {
                margin: 0;
                width: auto;
            }
            #header_shipToButton-popover {
                max-height: 75vh;
                padding: 0;
                margin: 0;
                border-radius: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                max-height: 90vh;
                            `,
                            null,
                            null,
                            css`
                                max-height: 90vh;
                            `,
                        ],
                        "max",
                    )}
                /* width */
                    ::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                }

                /* Track */
                ::-webkit-scrollbar-track {
                    box-shadow: inset 0 0 3px ${colorPallete.grey400};
                    border-radius: 6px;
                }

                /* Handle */
                ::-webkit-scrollbar-thumb {
                    background: ${colorPallete.grey800};
                    border-radius: 6px;
                    cursor: pointer;
                }

                /* Handle on hover */
                ::-webkit-scrollbar-thumb:hover {
                    background: ${colorPallete.grey200};
                }
            }
            .dynamicDropdownCustom {
                line-height: 0;
                &.activePopover {
                    line-height: normal;
                }
            }
            .findLocationModal {
                .popover-click-button {
                    display: flex;
                }
            }
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isAuthenticated,
    isSessionCreated,
    pageTitle,
    cart,
    cartUrl,
    homePageUrl,
    displayVmiNavigation
}: Props) => {
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    let scrollToRequestAccountFormSection;

    if (typeof window !== "undefined") {
        scrollToRequestAccountFormSection = document.querySelector(".scrollToRequestAccountForm");

        if (scrollToRequestAccountFormSection) {
            scrollToRequestAccountFormSection.addEventListener("click", function () {
                const requestDsgPlusForm = document.querySelector(".requestDsgPlusFormSection");

                if (requestDsgPlusForm) {
                    requestDsgPlusForm.scrollIntoView({ behavior: "smooth" });
                }
            });
        }
    }

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useLayoutEffect(() => {
        const authMessageWrapper = document.querySelector(".shipAuthMsgWrapper.authMsg");
        !authMessageWrapper?.firstElementChild?.firstElementChild?.hasChildNodes()
            ? authMessageWrapper?.classList.add("d-none")
            : authMessageWrapper.classList.remove("d-none");
    });

    useEffect(() => {
        if (!getCookie("alertCookies")) {
            setIsAlertOpen(true);
        }
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const setAlertCookies = () => {
        setIsAlertOpen(false);
        setCookie("alertCookies", "true", { expires: 365 });
    };

    let isEnablePayBillTrust = false;
    if (isSessionCreated != null && isSessionCreated !== undefined && isSessionCreated.indexOf("Billtrust") > -1) {
        isEnablePayBillTrust = true;
    }
    let isCheckoutPage = false;
    if (pageTitle === "Payment And Review") {
        isCheckoutPage = true;
    }

    const afterHeader = React.createRef<HTMLSpanElement>();

    return (
        <>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            {!displayVmiNavigation && (
                <Page
                    as="header"
                    isHeader={true}
                    {...styles.page}
                    id="pageHeader"
                    className={`${isAuthenticated ? "loggedInHeader" : "guestHeader"} ${
                        isCheckoutPage ? "checkoutPageHeader" : ""}`}
                >
                    <>
                        <SkipNav
                            extendedStyles={styles.skipToContent}
                            text={translate("Skip to main content")}
                            destination={afterHeader}
                        />
                        <EagerLoadingLogoProvider value={true}>
                            <GridContainer
                                gap={0}
                                {...styles.mainHeaderContainer}
                                className={`${isCheckoutPage ? "hideOnCheckoutPage" : ""}`}
                            >
                                <GridContainer gap={0} className="top-header" {...styles.topHeader}>
                                    <GridItem
                                        width={6}
                                        align="middle"
                                        className="nearest-branch-info"
                                        {...styles.nearestBranchInfo}
                                    >
                                        <Zone contentId={id} zoneName="DSGNearestBranchInformation" />
                                    </GridItem>
                                    <GridItem
                                        width={6}
                                        align="middle"
                                        className="header-link-list website-header-link-list"
                                        {...styles.headerLinkList}
                                    >
                                        <Zone contentId={id} zoneName="DSGStaticLinks" />
                                    </GridItem>
                                </GridContainer>
                                <GridContainer gap={0} className="main-header" {...styles.mainHeader}>
                                    <GridContainer gap={0}>
                                        <GridItem width={2} className="header-logo" {...styles.headerLogo}>
                                            <Zone contentId={id} zoneName="DSGLogo" />
                                        </GridItem>
                                        <GridItem width={4} className="header-main-navigation" {...styles.mainNavigation}>
                                            <Zone contentId={id} zoneName="DSGMainNavigation" />
                                        </GridItem>
                                        <GridItem width={4} className="header-search" {...styles.headerSearch}>
                                            <Zone contentId={id} zoneName="DSGSearchInput" />
                                        </GridItem>
                                        <GridItem width={1} className="header-reorder" {...styles.headerReorder}>
                                            <Zone contentId={id} zoneName="DSGReorderLink" />
                                        </GridItem>
                                        <GridItem
                                            width={1}
                                            className={`header-my-account ${isAuthenticated ? "hide-on-mobile" : ""}`}
                                            {...styles.headerMyAccount}
                                        >
                                            <Zone contentId={id} zoneName="DSGMyAccount" />
                                        </GridItem>
                                        <GridItem
                                            className={`hide-on-desktop ${!isAuthenticated ? "hide" : ""}`}
                                            {...styles.dsgCart}
                                        >
                                            <Zone contentId={id} zoneName="DSGCart" />
                                        </GridItem>
                                    </GridContainer>
                                </GridContainer>
                                <GridContainer
                                    gap={0}
                                    className={`websiteHeader ${
                                        isAlertOpen && !isAuthenticated ? "sub-header d-none" : "hideAlertBackground"
                                    }`}
                                    {...styles.subHeader}
                                >
                                    <GridContainer gap={0} isFullWidth={false} {...styles.subHeaderInner}>
                                        {isAuthenticated && (
                                            <>
                                                <GridItem width={6} align="middle" className="dsg-shipping-address">
                                                    <Zone contentId={id} zoneName="DSGShippingAddress" />
                                                </GridItem>
                                                {isEnablePayBillTrust && (
                                                    <>
                                                        <StyledWrapper
                                                            {...styles.subHeaderLinksStyles}
                                                            className="external-wrapper hide-on-mobile hide-on-tablet"
                                                        >
                                                            <GridItem width={2} {...styles.payMyBills} {...styles.dsgLinks}>
                                                                <Zone contentId={id} zoneName="DSGPayBillLinks" />
                                                            </GridItem>
                                                            <GridItem width={2} {...styles.dsgLinks}>
                                                                <Zone contentId={id} zoneName="DSGLinks" />
                                                            </GridItem>
                                                            <GridItem width={2} {...styles.dsgCart}>
                                                                <Zone contentId={id} zoneName="DSGCart" />
                                                            </GridItem>
                                                        </StyledWrapper>
                                                    </>
                                                )}
                                                {!isEnablePayBillTrust && (
                                                    <StyledWrapper
                                                        {...styles.subHeaderLinksStyles}
                                                        className="external-wrapper hide-on-mobile hide-on-tablet"
                                                    >
                                                        <GridItem width={3} {...styles.dsgLinks}>
                                                            <Zone contentId={id} zoneName="DSGLinks" />
                                                        </GridItem>
                                                        <GridItem width={3} {...styles.dsgCart}>
                                                            <Zone contentId={id} zoneName="DSGCart" />
                                                        </GridItem>
                                                    </StyledWrapper>
                                                )}
                                            </>
                                        )}
                                    </GridContainer>
                                </GridContainer>{" "}
                                {/**  Sub header ends here */}
                                {isAlertOpen && (
                                    <StyledWrapper className={`shipAuthMsgWrapper ${isAuthenticated ? "authMsg" : ""}`}>
                                        <GridItem
                                            width={12}
                                            className={`shippingGuestMessage ${isAuthenticated ? "authMsg" : ""}`}
                                            {...styles.shippingGuestMessage}
                                            align="middle"
                                        >
                                            <Zone contentId={id} zoneName="DSGGuestMessage" />
                                            <Icon src={AlertTriangle} size={24} className="alert-icon" />
                                            <Icon
                                                src={X}
                                                size={24}
                                                className="alert-icon close-icon"
                                                onClick={setAlertCookies}
                                            />
                                        </GridItem>
                                    </StyledWrapper>
                                )}
                            </GridContainer>
                        </EagerLoadingLogoProvider>
                        <span ref={afterHeader} tabIndex={-1} />
                    </>
                    <GridContainer gap={0} isFullWidth={false} {...styles.findLocationAnyWhere}>
                        <GridItem width={[12, 12, 12, 8, 8]}></GridItem>
                        <GridItem width={[12, 12, 12, 4, 4]} className="findLocationModal">
                            <Zone contentId={id} zoneName="FindLocationAnyWhere" />
                        </GridItem>
                    </GridContainer>
                    <Zone contentId={id} zoneName="DSGAddToCartModalWrapper" />
                    {isCheckoutPage && (
                        <StyledWrapper {...styles.checkoutHeader}>
                            <Typography className="back-to-cart">
                                <Link className="text-link text-link-primary" href={cartUrl}>
                                    <Icon src={ChevronDown} size={16} color={colorPallete.primaryRed500} />
                                    <span>
                                        {translate("Back to cart")}{" "}
                                        {cart && cart?.cartLines && cart?.cartLines?.length >= 0
                                            ? cart?.cartLines?.length > 1
                                                ? `(${cart?.cartLines?.length} items)`
                                                : `(${cart?.cartLines?.length} item)`
                                            : ""}
                                    </span>
                                </Link>
                            </Typography>
                            <StyledWrapper className="dsg-logo-large">
                                <Zone contentId={id} zoneName="DSGLogo" />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </Page>
            )}
            {displayVmiNavigation && (
                <Page
                    as="header"
                    isHeader={true}
                    {...styles.vmiPageHeader}
                    id="pageHeader"
                    className={`${isAuthenticated ? "loggedInHeader" : "guestHeader"} ${
                        isCheckoutPage ? "checkoutPageHeader vmiCheckout" : ""}`}
                >
                    <>
                        <SkipNav
                            extendedStyles={styles.skipToContent}
                            text={translate("Skip to main content")}
                            destination={afterHeader}
                        />
                        <EagerLoadingLogoProvider value={true}>
                            <GridContainer
                                gap={0}
                                {...styles.mainHeaderContainer}
                                className={`${isCheckoutPage ? "hideOnCheckoutPage" : ""}`}
                            >
                                <GridContainer gap={0} className="top-header" {...styles.topHeader}>
                                    {/* <GridItem
                                        width={6}
                                        align="middle"
                                        className="nearest-branch-info"
                                        {...styles.nearestBranchInfo}
                                    >
                                        <Zone contentId={id} zoneName="DSGNearestBranchInformation" />
                                    </GridItem> */}
                                    <GridItem
                                        width={12}
                                        align="middle"
                                        className="header-link-list"
                                        {...styles.headerVMILinkList}
                                    >
                                        <Zone contentId={id} zoneName="DSGVMIStaticLinks" />
                                    </GridItem>
                                </GridContainer>
                                <GridContainer gap={0} className="vmi-main-header main-header" {...styles.mainHeader}>
                                    <GridContainer gap={0}>
                                        <GridItem width={2} className="header-logo dsgVmiLogo" {...styles.headerLogo}>
                                            <Zone contentId={id} zoneName="DSGVMILogo" />
                                        </GridItem>
                                        <GridItem width={4} className="header-main-navigation" {...styles.mainNavigation}>
                                            <Zone contentId={id} zoneName="DSGMainNavigation" />
                                        </GridItem>
                                        <GridItem width={4} className="header-search" {...styles.headerSearch}>
                                            <Zone contentId={id} zoneName="DSGSearchInput" />
                                        </GridItem>
                                        {/* <GridItem width={1} className="header-reorder" {...styles.headerReorder}>
                                            <Zone contentId={id} zoneName="DSGReorderLink" />
                                        </GridItem> */}
                                        <GridItem
                                            width={2}
                                            className={`vmi-header-account header-my-account ${isAuthenticated ? "hide-on-mobile" : ""}`}
                                            {...styles.headerMyAccount}
                                        >
                                            <Zone contentId={id} zoneName="DSGMyAccount" />
                                        </GridItem>
                                        <GridItem
                                            className={`vmiCart hide-on-desktop ${!isAuthenticated ? "hide" : ""}`}
                                            {...styles.dsgCart}
                                        >
                                            <Zone contentId={id} zoneName="DSGCart" />
                                        </GridItem>
                                    </GridContainer>
                                </GridContainer>
                                <GridContainer
                                    gap={0}
                                    className={`vmiSubHeader ${
                                        isAlertOpen && !isAuthenticated ? "sub-header d-none" : "hideAlertBackground"
                                    }`}
                                    {...styles.subHeader}
                                >
                                    <GridContainer gap={0} isFullWidth={false} {...styles.subHeaderInner}>
                                        {isAuthenticated && (
                                            <>
                                                <GridItem width={6} align="middle" className="dsg-shipping-address vmi-address">
                                                    <Zone contentId={id} zoneName="DSGShippingAddress" />
                                                </GridItem>
                                                {isEnablePayBillTrust && (
                                                    <>
                                                        <StyledWrapper
                                                            {...styles.subHeaderLinksStyles}
                                                            className="external-wrapper hide-on-mobile hide-on-tablet"
                                                        >
                                                            <GridItem width={2} {...styles.payMyBills} className="vmiLinks" {...styles.dsgLinks}>
                                                                <Zone contentId={id} zoneName="DSGVMIPayBillLinks" />
                                                            </GridItem>
                                                            {/* <GridItem width={2} {...styles.dsgLinks}>
                                                                <Zone contentId={id} zoneName="DSGLinks" />
                                                            </GridItem> */}
                                                            <GridItem width={2} {...styles.dsgCart} className="vmiCart">
                                                                <Zone contentId={id} zoneName="DSGCart" />
                                                            </GridItem>
                                                        </StyledWrapper>
                                                    </>
                                                )}
                                                {!isEnablePayBillTrust && (
                                                    <StyledWrapper
                                                        {...styles.subHeaderLinksStyles}
                                                        className="external-wrapper hide-on-mobile hide-on-tablet"
                                                    >
                                                        {/* <GridItem width={3} {...styles.dsgLinks}>
                                                            <Zone contentId={id} zoneName="DSGLinks" />
                                                        </GridItem> */}
                                                        <GridItem width={3} {...styles.dsgCart} className="vmiCart">
                                                            <Zone contentId={id} zoneName="DSGCart" />
                                                        </GridItem>
                                                    </StyledWrapper>
                                                )}
                                            </>
                                        )}
                                    </GridContainer>
                                </GridContainer>{" "}
                                {/**  Sub header ends here */}
        
                            </GridContainer>
                        </EagerLoadingLogoProvider>
                        <span ref={afterHeader} tabIndex={-1} />
                    </>
                    <GridContainer gap={0} isFullWidth={false} {...styles.findLocationAnyWhere}>
                        <GridItem width={[12, 12, 12, 8, 8]}></GridItem>
                        <GridItem width={[12, 12, 12, 4, 4]} className="findLocationModal">
                            <Zone contentId={id} zoneName="FindLocationAnyWhere" />
                        </GridItem>
                    </GridContainer>
                    <Zone contentId={id} zoneName="DSGAddToCartModalWrapper" />
                    {isCheckoutPage && (
                        <StyledWrapper {...styles.checkoutHeader} className="vmiCheckout">
                            <Typography className="back-to-cart">
                                <Link className="text-link text-link-primary" href={cartUrl}>
                                    <Icon src={ChevronDown} size={16} color={colorPallete.primaryRed500} />
                                    <span>
                                        {translate("Back to cart")}{" "}
                                        {cart && cart?.cartLines && cart?.cartLines?.length >= 0
                                            ? cart?.cartLines?.length > 1
                                                ? `(${cart?.cartLines?.length} items)`
                                                : `(${cart?.cartLines?.length} item)`
                                            : ""}
                                    </span>
                                </Link>
                            </Typography>
                            <StyledWrapper className="dsg-logo-large">
                                <Zone contentId={id} zoneName="DSGVMILogo" />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </Page>
            )}
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
